<script lang="ts" setup>
import * as icons from 'lucide-vue-next'
import type { LucideIcon as LucideIconType } from 'lucide-vue-next'
import { getThemeSettings } from '@/lib'
import { computed } from 'vue'

interface IconProps {
  variant: string
  strokeWidth?: number
  type?: 'primary' | 'accent' | 'success' | 'info' | 'warning' | 'error'
}

const props = withDefaults(defineProps<IconProps>(), {
  strokeWidth: undefined,
  type: undefined,
})

const { iconWeight } = getThemeSettings()

const effectiveStrokeWidth = computed(() => props.strokeWidth || iconWeight || 2)

// Convert kebab-case to PascalCase for Lucide icon names
const iconName = computed(() => {
  const name = props.variant
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join('')
  return name as keyof typeof icons
})

const LucideIcon = computed(() => {
  const icon = icons[iconName.value] as LucideIconType | undefined
  if (!icon) {
    console.warn(`Icon ${props.variant} not found in lucide-vue-next`)
    return null
  }
  return icon
})

const iconClasses = computed(() => ['icon', props.type ? `icon--${props.type}` : null])
</script>

<template>
  <component
    v-if="LucideIcon"
    :is="LucideIcon"
    :size="24"
    :stroke-width="effectiveStrokeWidth"
    :class="iconClasses"
  />
</template>

<style scoped>
.icon--primary {
  color: var(--color-primary);
}
.icon--accent {
  color: var(--color-accent);
}
.icon--success {
  color: var(--color-success);
}
.icon--info {
  color: var(--color-info);
}
.icon--warning {
  color: var(--color-warning);
}
.icon--error {
  color: var(--color-error);
}
</style>
